import { effect, inject, Injectable, signal } from '@angular/core';
import { icon } from '@fortawesome/fontawesome-svg-core';
import { faMap } from '@fortawesome/pro-regular-svg-icons';
import {
    AddLayerObject,
    LayerSpecification,
    Map as MapLibreMap,
    SourceSpecification,
    StyleSpecification,
} from 'maplibre-gl';

import LIBERTY_STYLE from '@connect-services/client/assets/styles/LIBERTY_STYLE.json';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { ProjectSessionStorageService } from '@connect-services/client/services/project-session-storage.service';

export enum TileSourceTypeEnum {
    BLACK = 'black',
    DATAVIZ = 'dataviz',
    LIBERTY = 'liberty',
    OSM = 'osm',
    SATELLITE = 'satellite',
    WATERCOLOR = 'watercolor',
    WHITE = 'white',
}

export enum BasemapLayerType {
    BACKGROUND = 'BACKGROUND',
    RASTER = 'RASTER',
}

export enum Contribution {
    MAPTILER = '&copy; MapTiler Contributors',
    OPENFREEMAP = '$copy; OpenFreeMap © OpenMapTiles Data from OpenStreetMap',
    OPENSTREETMAP = '&copy; OpenStreetMap Contributors',
}

export type BasemapInfo = {
    attribution?: string;
    color?: string;
    layerType: BasemapLayerType;
    sourceId?: string;
    thumbnail?: string;
    type: TileSourceTypeEnum;
    url?: string;
};

export type BasemapSecondary = {
    opacity: number;
    type: TileSourceTypeEnum;
};

export type BasemapState = {
    primary: TileSourceTypeEnum;
    secondaries: BasemapSecondary[];
};

export const TERRAIN_SOURCE_ID = 'terrainSource';

export const BASEMAPS_CATALOG: Record<TileSourceTypeEnum, BasemapInfo> = {
    [TileSourceTypeEnum.DATAVIZ]: {
        attribution: Contribution.MAPTILER,
        layerType: BasemapLayerType.RASTER,
        sourceId: 'dataviz',
        thumbnail: 'assets/images/basemap/dataviz.png',
        type: TileSourceTypeEnum.DATAVIZ,
        url: 'https://api.maptiler.com/maps/dataviz/{z}/{x}/{y}.png?key=jylIx0Emb0QilIHghIF8',
    },
    [TileSourceTypeEnum.LIBERTY]: {
        attribution: Contribution.OPENFREEMAP,
        layerType: BasemapLayerType.RASTER,
        thumbnail: 'assets/images/basemap/liberty.png',
        type: TileSourceTypeEnum.LIBERTY,
    },
    [TileSourceTypeEnum.OSM]: {
        attribution: Contribution.OPENSTREETMAP,
        layerType: BasemapLayerType.RASTER,
        sourceId: 'osm',
        thumbnail: 'assets/images/basemap/osm.png',
        type: TileSourceTypeEnum.OSM,
        url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    },
    [TileSourceTypeEnum.SATELLITE]: {
        attribution: Contribution.MAPTILER,
        layerType: BasemapLayerType.RASTER,
        sourceId: 'satellite',
        thumbnail: 'assets/images/basemap/satellite.png',
        type: TileSourceTypeEnum.SATELLITE,
        url: 'https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key=jylIx0Emb0QilIHghIF8',
    },
    [TileSourceTypeEnum.WATERCOLOR]: {
        attribution: Contribution.MAPTILER,
        layerType: BasemapLayerType.RASTER,
        sourceId: 'watercolor',
        thumbnail: 'assets/images/basemap/watercolor.png',
        type: TileSourceTypeEnum.WATERCOLOR,
        url: 'https://api.maptiler.com/maps/aquarelle/{z}/{x}/{y}.png?key=jylIx0Emb0QilIHghIF8',
    },
    [TileSourceTypeEnum.WHITE]: {
        color: '#ffffff',
        layerType: BasemapLayerType.BACKGROUND,
        type: TileSourceTypeEnum.WHITE,
    },
    [TileSourceTypeEnum.BLACK]: {
        color: '#000000',
        layerType: BasemapLayerType.BACKGROUND,
        type: TileSourceTypeEnum.BLACK,
    },
};

export const ALL_BASEMAP_TYPES: readonly TileSourceTypeEnum[] = Object.values(TileSourceTypeEnum);

export const DEFAULT_BASEMAP_STATE: BasemapState = {
    primary: TileSourceTypeEnum.OSM,
    secondaries: [],
};

export const DEFAULT_SECONDARY_OPACITY = 0.5;

@Injectable({
    providedIn: 'root',
})
export class BasemapService {
    public button: HTMLButtonElement = document.createElement('button');
    public container: HTMLElement | null = null;
    public readonly isActive = signal<boolean>(false);
    public isTerrainElevationActive: boolean = false;
    public readonly primary = signal<TileSourceTypeEnum>(DEFAULT_BASEMAP_STATE.primary);
    public readonly secondaries = signal<BasemapSecondary[]>(DEFAULT_BASEMAP_STATE.secondaries);

    private lastLoadedProjectId: string | null = null;
    private map: MapLibreMap | null = null;
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectSessionsStorage = inject(ProjectSessionStorageService);

    public constructor() {
        effect(() => {
            if (!this.projectBloc.value()) {
                return;
            }

            const projectId = this.projectBloc.value()?.id;
            if (!projectId || projectId === this.lastLoadedProjectId) {
                return;
            }

            const configuredBaseMap = this.projectSessionsStorage.getBasemapAndTerrainElevation(projectId);
            this.lastLoadedProjectId = projectId;

            this.isTerrainElevationActive = configuredBaseMap?.isTerrainElevationActive;

            if (configuredBaseMap) {
                this.primary.set(configuredBaseMap.basemap?.primary ?? DEFAULT_BASEMAP_STATE.primary);
                this.secondaries.set(configuredBaseMap.basemap?.secondaries ?? []);
                this.applyBasemapState();
                this.toggleTerrainElevation(configuredBaseMap.isTerrainElevationActive);

                return;
            }

            this.primary.set(DEFAULT_BASEMAP_STATE.primary);
            this.secondaries.set([]);
            this.applyBasemapState();
        });

        effect(() => {
            this.saveBasemapState();
        });
    }

    public addSecondary(type: TileSourceTypeEnum): void {
        if (type === this.primary() || this.secondaries().some((basemapSecondary) => basemapSecondary.type === type)) {
            return;
        }
        this.secondaries.set([...this.secondaries(), { opacity: DEFAULT_SECONDARY_OPACITY, type }]);
        this.applyBasemapState();
    }

    public bindMap(map: MapLibreMap): void {
        this.map = map;
        if (this.map.isStyleLoaded()) {
            this.applyBasemapState();

            return;
        }

        // we wait for the first “load” only once.
        this.map.once('load', () => {
            this.applyBasemapState();
            this.toggleTerrainElevation(this.isTerrainElevationActive);
        });
    }

    public changeSecondaryType(oldType: TileSourceTypeEnum, nextType: TileSourceTypeEnum): void {
        if (nextType === this.primary()) {
            return;
        }

        const hasOld = this.secondaries().some((basemapSecondary) => basemapSecondary.type === oldType);

        if (!hasOld) {
            return;
        }

        const hasNext = this.secondaries().some((basemapSecondary) => basemapSecondary.type === nextType);

        if (hasNext) {
            this.removeSecondary(oldType);

            return;
        }

        const updatedBasemap = this.secondaries().map((basemapSecondary) =>
            basemapSecondary.type === oldType
                ? {
                      ...basemapSecondary,
                      type: nextType,
                  }
                : basemapSecondary,
        );

        this.secondaries.set(updatedBasemap);
        this.applyBasemapState();
    }

    public removeSecondary(type: TileSourceTypeEnum): void {
        this.secondaries.set(this.secondaries().filter((basemapSecondary) => basemapSecondary.type !== type));
        this.applyBasemapState();
    }

    public selectPrimary(type: TileSourceTypeEnum): void {
        this.primary.set(type);
        if (this.secondaries().some((basemapSecondary) => basemapSecondary.type === type)) {
            this.removeSecondary(type);
        }
        this.applyBasemapState();
    }

    public setButtonIcon(): void {
        this.button.innerHTML = icon(faMap, {
            styles: {
                'background-color': this.getIconBackgroundColor(),
                'border-radius': '0.2rem',
                color: this.getIconColor(),
                height: '1.25rem',
                padding: '0.25rem',
                width: '1.25rem',
            },
        }).html[0];
    }

    public setSecondaryOpacity(type: TileSourceTypeEnum, firstOpacity: number): void {
        const newOpacity = Math.max(0, Math.min(1, firstOpacity));

        const updatedBasemapSecondaryOpacity = this.secondaries().map((basemapSecondary: BasemapSecondary) =>
            basemapSecondary.type === type
                ? {
                      ...basemapSecondary,
                      opacity: newOpacity,
                  }
                : basemapSecondary,
        );

        this.secondaries.set(updatedBasemapSecondaryOpacity);
        this.applyBasemapState();
    }

    public toggleTerrainElevation(isChecked: boolean): void {
        if (!this.map) {
            return;
        }

        if (this.projectBloc.value()) {
            this.projectSessionsStorage.updateTerrainElevation(this.projectBloc.value()!.id, isChecked);
        }

        this.isTerrainElevationActive = isChecked;

        if (isChecked) {
            this.activeTerrainElevation();

            return;
        }

        this.removeTerrainElevation();
    }

    private activeTerrainElevation(): void {
        if (!this.map) {
            return;
        }

        this.map.addSource(TERRAIN_SOURCE_ID, {
            attribution: 'Elevation tiles: AWS elevation-tiles-prod / Terrarium',
            encoding: 'terrarium',
            maxzoom: 15,
            tileSize: 256,
            tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
            type: 'raster-dem',
        });

        this.map.setTerrain({
            exaggeration: 1,
            source: TERRAIN_SOURCE_ID,
        });

        this.map.addLayer({
            id: 'terrain-hillshade',
            layout: {
                visibility: 'visible',
            },
            paint: {
                'hillshade-accent-color': '#8b7b6f',
                'hillshade-exaggeration': 0.35,
                'hillshade-highlight-color': '#fff8e8',
                'hillshade-shadow-color': '#473b24',
            },
            source: TERRAIN_SOURCE_ID,
            type: 'hillshade',
        });
    }

    private applyBasemapState(): void {
        if (!this.map?.isStyleLoaded()) {
            return;
        }

        this.removeAllPrimaryCandidatesExcept(this.primary());
        this.drawPrimaryLayer(this.primary(), this.primary());

        let insertIndex = this.findLayerIndex(this.primary());
        for (const basemapSecondary of this.secondaries()) {
            insertIndex = this.insertSecondaryLayerAfterIndex(
                basemapSecondary.type,
                basemapSecondary.opacity,
                insertIndex,
            );
        }
    }

    private buildLayerSpecification(type: TileSourceTypeEnum, opacity: number, id: string): LayerSpecification {
        const category = BASEMAPS_CATALOG[type];

        if (category.layerType === BasemapLayerType.BACKGROUND) {
            return {
                id,
                paint: {
                    'background-color': category.color,
                    'background-opacity': opacity,
                },
                type: 'background',
            };
        }

        return {
            id,
            paint: { 'raster-opacity': opacity },
            source: category.sourceId!,
            type: 'raster',
        };
    }

    private drawLibertyLayerAndSource(): void {
        Object.entries(LIBERTY_STYLE.sources).map(([sourceId, sourceInfo]) => {
            this.map!.addSource(sourceId, sourceInfo as SourceSpecification);
        });

        //I can't really type here because the data come from a JSON and complicate thing with enum value as a string
        LIBERTY_STYLE.layers.forEach((layer) => {
            this.map?.addLayer(layer as AddLayerObject);
        });
    }

    private drawPrimaryLayer(type: TileSourceTypeEnum, id: TileSourceTypeEnum): void {
        if (!this.map) {
            return;
        }

        this.removeLayerIfExists(id);

        if (type === TileSourceTypeEnum.LIBERTY) {
            this.drawLibertyLayerAndSource();

            return;
        }

        const firstId = this.getStyleLayers()[0]?.id;
        this.map.addLayer(this.buildLayerSpecification(type, 1, id), firstId);
    }

    private findLayerIndex(id: string): number {
        return this.getStyleLayers().findIndex(
            (layerSpecification: LayerSpecification) => layerSpecification.id === id,
        );
    }

    private getIconBackgroundColor(): string {
        return this.isActive()
            ? getComputedStyle(document.documentElement).getPropertyValue('--custom-light-blue').trim()
            : '';
    }

    private getIconColor(): string {
        return this.isActive() ? getComputedStyle(document.documentElement).getPropertyValue('--secondary').trim() : '';
    }

    private getInsertBeforeId(afterIndex: number): string | undefined {
        return this.getStyleLayers()[afterIndex + 1]?.id;
    }

    private getStyleLayers(): LayerSpecification[] {
        const style: StyleSpecification | undefined = this.map?.getStyle();

        return style?.layers ?? [];
    }

    private insertSecondaryLayerAfterIndex(type: TileSourceTypeEnum, opacity: number, afterIndex: number): number {
        if (!this.map) {
            return afterIndex;
        }

        this.removeLayerIfExists(type);
        this.map.addLayer(this.buildLayerSpecification(type, opacity, type), this.getInsertBeforeId(afterIndex));

        return this.findLayerIndex(type);
    }

    private removeAllPrimaryCandidatesExcept(keepId: string): void {
        const tileSourceTypeEnums = ALL_BASEMAP_TYPES.filter((tileSource: TileSourceTypeEnum) => tileSource !== keepId);

        for (const id of tileSourceTypeEnums) {
            this.removeLayerIfExists(id);
        }
    }

    private removeLayerIfExists(id: string): void {
        if (!this.map) {
            return;
        }

        if (id === TileSourceTypeEnum.LIBERTY) {
            this.removeLibertyLayerAndSource();

            return;
        }

        if (!this.map.getLayer(id)) {
            return;
        }

        this.map.removeLayer(id);
    }

    private removeLibertyLayerAndSource(): void {
        LIBERTY_STYLE.layers.forEach((layer) => {
            if (!this.map?.getLayer(layer.id)) {
                return;
            }

            this.map?.removeLayer(layer.id);
        });

        Object.keys(LIBERTY_STYLE.sources).forEach((sourceId) => {
            if (!this.map?.getSource(sourceId)) {
                return;
            }

            this.map?.removeSource(sourceId);
        });
    }

    private removeTerrainElevation(): void {
        if (!this.map) {
            return;
        }

        if (this.map.getLayer('terrain-hillshade')) {
            this.map.removeLayer('terrain-hillshade');
        }

        this.map.setTerrain(null);

        if (!this.map.getSource(TERRAIN_SOURCE_ID)) {
            return;
        }

        this.map.removeSource(TERRAIN_SOURCE_ID);
    }

    private saveBasemapState(): void {
        const projectId = this.lastLoadedProjectId;
        if (!projectId) {
            return;
        }
        const state: BasemapState = { primary: this.primary(), secondaries: this.secondaries() };
        this.projectSessionsStorage.setBasemap(projectId, state);
    }
}
